import { useEffect, useId, useRef, useState } from 'react';
import type { JSX } from 'react';

export interface CheckboxOption {
  value: string;
  label: string;
}

/**
 * Button that opens a checkbox menu. Not a native multi-select listbox.
 * Used for the events status filter and for event level. Tap targets are at least 44×44.
 */
export function CheckboxDropdown({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
  testId,
}: {
  label: string;
  options: readonly CheckboxOption[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  emptyLabel: string;
  testId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const menuId = useId();
  const summary =
    selected.length === 0
      ? emptyLabel
      : options
          .filter((option) => selected.includes(option.value))
          .map((option) => option.label)
          .join(', ');

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function toggle(value: string): void {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(options.map((option) => option.value).filter((item) => next.includes(item)));
  }

  return (
    <div className="field checkbox-dropdown" ref={rootRef}>
      <span id={labelId}>{label}</span>
      <button
        type="button"
        className="checkbox-dropdown__button"
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        data-testid={testId}
        onClick={() => setOpen((current) => !current)}
      >
        {summary}
      </button>
      {open ? (
        <div
          id={menuId}
          className="checkbox-dropdown__menu"
          role="group"
          aria-labelledby={labelId}
          data-testid={`${testId}-menu`}
        >
          {options.map((option) => (
            <label key={option.value} className="checkbox-dropdown__option">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                data-testid={`${testId}-${option.value}`}
              />
              {option.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
